import { CoreApplicationContext } from '@eventbrite/context-gen';
import { EBEnvs, getCurrentEnv } from '@eventbrite/feature-detection';
import { FeatureFlagsProvider } from '@eventbrite/feature-flag-utils';
import {
    getUser,
    StatsigSynchronousProvider,
    STATSIG_ENV,
} from '@eventbrite/statsig';
import React from 'react';
import { BrowserRouter, Route, StaticRouter, Switch } from 'react-router-dom';
import { HeroSectionProps } from './containers/HeroSection';
import { ThingsToDoBasePage } from './pages';
import { PointOfInterestPage } from './pages/point-of-interest';
import { ThingsToDoPage } from './pages/things-to-do';
import { PointOfInterestAppProps, ThingsToDoAppProps } from './types';
import { getLocation } from './utils';

const ENV_TO_STATSIG_ENV_MAP: { [key in EBEnvs]?: string } = {
    [EBEnvs.DEV]: STATSIG_ENV.DEV,
    [EBEnvs.QA]: STATSIG_ENV.QA,
    [EBEnvs.PROD]: STATSIG_ENV.PROD,
};

type RouteProps = (ThingsToDoAppProps | PointOfInterestAppProps) &
    CoreApplicationContext & {
        location_slug?: string;
        place_id?: string;
    };

export const Routes: React.FC<RouteProps> = (props) => {
    const Router = (
        process.env.REACT_APP_TARGET === 'server' ? StaticRouter : BrowserRouter
    ) as React.ElementType;

    const { env, user, featureFlags } = props;

    const location = getLocation(props);
    const languageSubdirectory = env.localeInfo.uses_language_subdirectory
        ? env.localeInfo.locale
        : '';
    const render = () => {
        props = props as React.PropsWithChildren<
            PointOfInterestAppProps & CoreApplicationContext
        >;

        const currentEnv = getCurrentEnv(props);

        return (
            <StatsigSynchronousProvider
                user={getUser(props.user)}
                initializeValues={props.options?.initializeValues}
                options={{
                    ...props.options,
                    environment: {
                        tier: currentEnv
                            ? ENV_TO_STATSIG_ENV_MAP[currentEnv]
                            : '',
                    },
                }}
            >
                <FeatureFlagsProvider value={props.featureFlags}>
                    <ThingsToDoBasePage
                        env={env}
                        user={user}
                        request={props.request}
                        featureFlags={featureFlags}
                        footerLinks={props.footerLinks}
                        breadcrumbs={props.breadcrumbs}
                        currentPlace={props.shortPlaceName}
                        location={location}
                    >
                        <PointOfInterestPage
                            nearbyEvents={props.nearbyEvents}
                            breadcrumbs={props.breadcrumbs}
                            pointOfInterest={props.pointOfInterest}
                            heroSection={props.heroSection}
                            nearbyPointsOfInterest={
                                props.nearbyPointsOfInterest
                            }
                            freeEvents={props.freeEvents}
                            nearbyNightlifeEvents={props.nearbyNightlifeEvents}
                            featureFlags={props.featureFlags}
                            query_location_data={props.query_location_data}
                            userId={props.user?.publicId}
                            languageSubdirectory={languageSubdirectory}
                        />
                    </ThingsToDoBasePage>
                </FeatureFlagsProvider>
            </StatsigSynchronousProvider>
        );
    };
    return (
        <Router
            location={props.request.path}
            context={{}}
            forceRefresh={props.isBotRequest}
        >
            <Switch>
                <Route
                    path="/:locale?/ttd/:locationSlug/"
                    exact={true}
                    render={({ match }) => {
                        const locale: string = match?.params?.locale;
                        props = props as React.PropsWithChildren<
                            ThingsToDoAppProps & CoreApplicationContext
                        >;
                        return (
                            <FeatureFlagsProvider value={props.featureFlags}>
                                <ThingsToDoBasePage
                                    env={env}
                                    user={user}
                                    request={props.request}
                                    featureFlags={featureFlags}
                                    footerLinks={props.footerLinks}
                                    breadcrumbs={props.breadcrumbs}
                                    currentPlace={props.shortPlaceName}
                                    location={location}
                                >
                                    <ThingsToDoPage
                                        env={env}
                                        breadcrumbs={props.breadcrumbs}
                                        trendingSearchesResult={
                                            props.trendingSearchesResult
                                        }
                                        nearbyCitiesResult={
                                            props.nearbyCitiesResult
                                        }
                                        categories={props.categories}
                                        topEventsData={props.topEventsData}
                                        heroSection={
                                            props.heroSection as HeroSectionProps
                                        }
                                        topOrganizers={props.topOrganizers}
                                        editorialCollections={
                                            props.editorial_collections
                                        }
                                        nearbyEvents={props.nearbyEvents}
                                        nearbyFreeEvents={
                                            props.nearbyFreeEvents
                                        }
                                        nearbyNightlifeEvents={
                                            props.nearbyNightlifeEvents
                                        }
                                        point_of_interest_shelf={
                                            props.point_of_interest_shelf
                                        }
                                        featureFlags={props.featureFlags}
                                        query_location_data={
                                            props.query_location_data
                                        }
                                        userId={props.user?.publicId}
                                        place_id={props.place_id}
                                        location_slug={
                                            props.location_slug || ''
                                        }
                                        language_code={
                                            props.env.localeInfo.language_code
                                        }
                                        locale={locale}
                                        languageSubdirectory={
                                            languageSubdirectory
                                        }
                                    />
                                </ThingsToDoBasePage>
                            </FeatureFlagsProvider>
                        );
                    }}
                />
                <Route
                    path="/:locale?/poi/:location_slug/:point_of_interest_id"
                    exact={true}
                    render={render}
                />
                {/*
                    Core won't perform redirects in local dev, so a dev route is required.
                    In production, this route, /poi/:google_place_id, is redirected to
                    /poi/:location_slug/:point_of_interest_id before it hits the front-end.
                    */}
                {props.request.lifecycle !== 'production' && (
                    <Route
                        path="/:locale?/poi/:google_place_id"
                        exact={true}
                        render={render}
                    />
                )}
            </Switch>
        </Router>
    );
};
